<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-btn v-if="!useForSelection" class="ml-2" color="primary" v-on:click="createNewKennel">+ New Kennel</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn>&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :footer-props="footerProps"      
      :show-select="useForSelection">
        <template v-slot:item.data-table-select="{ item }">
          <v-btn
            v-if="useForSelection"
            class="mt-2"
            icon
            v-on:click="() => onRowSelected(item)"
            color="primary"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.registrationDate="{ item }">
          <span v-if="!!item.registrationDate">
            <localized-date :date="item.registrationDate" />
          </span>
        </template>
        <template v-slot:item.expiryDate="{ item }">
          <span v-if="!!item.expiryDate">
            <localized-date :date="item.expiryDate" />
          </span>
          <span v-else>
            Permanent
          </span>
        </template>
        <template v-slot:item.kennelId="{ item }">
          <router-link :to="`/admin/kennels/${item.kennelId}`" target="_blank">
            <span> {{ item.name }} - {{ item.kennelId }} </span>
          </router-link>       
        </router-link>       
          </router-link>       
        </template>
        <template v-slot:item.isHOF="{ item }">
          <span>
            HOF: {{ item.isHOF }} <template v-if="item.isHOF && item.hofDate != null">(<localized-date :date="item.hofDate" />)</template>
          </span><br/>
          <span>
            HOFX: {{ item.isHOFX }} <template v-if="item.isHOFX && item.hofxDate != null">(<localized-date :date="item.hofxDate" />)</template>
          </span><br/>
          <span>
            SKE: {{ item.isSKE }} <template v-if="item.isSKE && item.skeDate != null">(<localized-date :date="item.skeDate" />)</template>
          </span><br/>
        </template>
        <template v-slot:item.owners="{ item }">
          <CoownersList
            :owners="item.owners"
            :ownershipType="kennelOwnershipType"
            :hideHeader="true"
          />
        </template>
        <template v-slot:header.kennelId="{ header }">
          <v-text-field
            hide-details
            v-model="searchParams.kennelId"
            label="Kennel ID"
            clearable
            @click:clear="searchParams.kennelId = ''"
            v-on:keyup.enter="applyFilter"
          />&nbsp;
          <v-text-field
            hide-details
            class="mt-0 pt-0 mb-2"
            v-model="searchParams.Name"
            label="Name"
            clearable
            @click:clear="searchParams.Name = ''"
            v-on:keyup.enter="applyFilter"
          />
        </template>
        <template v-slot:header.registrationDate>
          <div width="300px">
            <datepicker
              :date="searchParams.registrationDateFrom"
              label="Registration Date From"
              v-on:updateDates="updateDates($event, 'registrationDateFrom')"
              v-on:enter="updateDateSearch($event, 'registrationDateFrom')"
            />
            <datepicker
              :date="searchParams.registrationDateTo"
              label="Registration Date To"
              v-on:updateDates="updateDates($event, 'registrationDateTo')"
              v-on:enter="updateDateSearch($event, 'registrationDateTo')"
            />
          </div>
        </template>
        <template v-slot:header.expiryDate>
          <div width="300px">
            <datepicker
              :date="searchParams.expiryDateFrom"
              label="Expiry Date From"
              v-on:updateDates="updateDates($event, 'expiryDateFrom')"
              v-on:enter="updateDateSearch($event, 'expiryDateFrom')"
            />
            <datepicker
              :date="searchParams.expiryDateTo"
              label="Expiry Date To"
              v-on:updateDates="updateDates($event, 'expiryDateTo')"
              v-on:enter="updateDateSearch($event, 'expiryDateTo')"
            />
            <v-checkbox
              :indeterminate="searchParams.permanent == null"
              v-model="searchParams.permanent"
              label="Permanent"
              v-on:change="toggleCheckbox"
            />
          </div>
        </template>
        <template v-slot:header.owners="{ header }">
          <v-text-field
            hide-details
            class="mt-0 pt-0 mb-2"
            v-model="searchParams.ownerName"
            label="Owner/Co-Owner Name"
            clearable
            @click:clear="searchParams.ownerName = ''"
            v-on:keyup.enter="applyFilter"
          />
          <v-text-field
            v-model="searchParams.memberId"
            label="Member ID"
            clearable
            @click:clear="searchParams.memberId = ''"
            v-on:keyup.enter="applyFilter"
          />
        </template>
        <template v-slot:header.isHOF="{ header }">
          <div>
            <v-checkbox
              :indeterminate="searchParams.isHOF == null"
              v-on:change="toggleHofCheckbox"
              v-model="searchParams.isHOF"
              label="Hall of Fame"
            />
            <v-checkbox
              :indeterminate="searchParams.isHOFX == null"
              v-on:change="toggleHofxCheckbox"
              v-model="searchParams.isHOFX"
              label="Hall of Fame Excellent"
            />
          </div>
            <v-checkbox
              :indeterminate="searchParams.isSKE == null"
              v-on:change="toggleSkeCheckbox"
              v-model="searchParams.isSKE"
              label="Stockdog Kennel of Excellence"
            />
        </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import kennelService from "@/services/admin/kennelService";
import { getErrorMessage } from "@/services/errorHandler";

import { MetaNames } from "@/constants";
import { UserInfo } from "@/types/User";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "KennelList",
  components: {
    CoownersList,
    Datepicker,
    PaginationSelect
  },
  props: {
    useForSelection: { type: Boolean },
    onRowSelected: { type: Function },
  },
  data: function () {
    return {
      title: MetaNames.AdminKennels,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        kennelId: "",
        kennelName: "",
        ownerName: "",
        memberId: "",
        isHOF: null as any,
        isHOFX: null as any,
        isSKE: null as any,
        registrationDateFrom: "",
        registrationDateTo: "",  
        expiryDateFrom: "",
        expiryDateTo: "",      
        permanent: null  as boolean | null, 
      },
      currentCheckboxIdx: 0,
      options: {} as PaginationOptions,
      headers: [
        { text: "Kennel ID", align: "left", value: "kennelId" , width: "20%", sortable: false },
        { text: "Owners", align: "left", value: "owners", width: "25%", sortable: false },
        { text: "Hall of Fame", align: "left", value: "isHOF", width: "15%", sortable: false },
        { text: "Registration date", align: "left", value: "registrationDate", width: "20%", sortable: false },       
        { text: "Expiry date", align: "left", value: "expiryDate", width: "20%", sortable: false },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
      currentIsHOFIdx: 0,
      currentIsHOFXIdx: 0,
      currentIsSKEIdx: 0,
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    toggleCheckbox: function () {
      var states = [null, true, false];
      var next = ++this.currentCheckboxIdx % states.length;
      this.currentCheckboxIdx = next;
      this.searchParams.permanent = states[next];
    },
    createNewKennel: function() {
      this.$navigate({path: '/admin/kennels'});
    },
    toggleHofCheckbox: function() {
      var states = [null, true, false];
      var next = ++this.currentIsHOFIdx % states.length;
      this.currentIsHOFIdx = next;
      this.searchParams.isHOF = states[next];
    },
    toggleHofxCheckbox: function() {
      var states = [null, true, false];
      var next = ++this.currentIsHOFXIdx % states.length;
      this.currentIsHOFXIdx = next;
      this.searchParams.isHOFX = states[next];
    },
    toggleSkeCheckbox: function() {
      var states = [null, true, false];
      var next = ++this.currentIsSKEIdx % states.length;
      this.currentIsSKEIdx = next;
      this.searchParams.isSKE = states[next];
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        kennelId: "",
        kennelName: "",
        ownerName: "",
        memberId: "",
        isHOF: null,
        isHOFX: null,
        isSKE: null,
        registrationDateFrom: "",
        registrationDateTo: "", 
        expiryDateFrom: "",
        expiryDateTo: "",    
        permanent: null,   
      };
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await kennelService.getKennelsList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    },
    updateDates: function (val: any, key: string) {
      (this as any).searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: any, key: string) {
      await this.updateDates(val, key);
      this.applyFilter();
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    kennelOwnershipType: function () {
      return OwnershipType.Kennel;
    },
  },
});
</script>
